import { C, colHead } from '../theme'
import { grp, unitsToNumber, usd } from '../lib/format'
import { TokenIcon, tokenName, type TokenSymbol } from './ui/TokenIcon'
import { ASSET_ORDER, PAUSE_LABEL } from '../data/mock/constants'
import type { Asset, Snapshot } from '../types'

const PANEL: React.CSSProperties = {
  minWidth: 0,
  border: `1px solid ${C.border}`,
  padding: '14px 16px 8px',
}

const TITLE: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: '.02em',
}

const TOTAL: React.CSSProperties = {
  fontSize: 30,
  fontWeight: 800,
  letterSpacing: '-0.02em',
  color: C.accent,
  whiteSpace: 'nowrap',
}

function Row({
  symbol,
  amount,
  amountUsd,
  decimals,
  right,
}: {
  symbol: TokenSymbol
  amount: number
  amountUsd: number
  decimals: number
  right?: React.ReactNode
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '22px minmax(0,1fr) auto auto',
        gap: 10,
        alignItems: 'center',
        padding: '10px 0',
        borderBottom: `1px solid ${C.rule}`,
        whiteSpace: 'nowrap',
      }}
    >
      <TokenIcon symbol={symbol} />
      <span style={{ minWidth: 0 }}>
        <span style={{ fontSize: 15, fontWeight: 600 }}>{symbol}</span>
        <span
          style={{
            fontSize: 12,
            color: C.muted,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {tokenName(symbol)}
        </span>
      </span>
      <span style={{ textAlign: 'right' }}>
        <span style={{ fontSize: 15 }}>{grp(amount.toFixed(decimals))}</span>
        <span style={{ fontSize: 12, color: C.muted, display: 'block' }}>{usd(amountUsd)}</span>
      </span>
      {right ?? <span />}
    </div>
  )
}

/**
 * Верхний блок — то же, что на витрине венью, но с числами, которые там
 * отсутствуют: рядом с их health factor стоит настоящее расстояние до
 * ликвидации, а рядом с залогом — сколько из него ещё можно занять.
 */
export function Lending({ snap }: { snap: Snapshot }) {
  const { pool, derived: d } = snap
  const refPrice = Number(pool.assets[pool.usdReferenceAsset].oraclePriceUnits)

  const priceOf = (sym: Asset) => Number(pool.assets[sym].oraclePriceUnits) / refPrice
  const amountOf = (sym: Asset, field: 'debtUnits' | 'lockedCollateralUnits') =>
    unitsToNumber(pool.assets[sym][field], pool.assets[sym].decimals)

  const borrowed = ASSET_ORDER.filter((s) => pool.assets[s].debtUnits > 0n)
  const collateral = ASSET_ORDER.filter((s) => pool.assets[s].lockedCollateralUnits > 0n)

  return (
    <div
      data-hero
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 16,
        marginTop: 20,
      }}
    >
      {/* ── Долг ───────────────────────────────────────────────────── */}
      <div style={PANEL}>
        <div style={TITLE}>Borrowed</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap', marginTop: 6 }}>
          <span style={TOTAL}>{usd(d.debtUsd)}</span>
          <span style={{ fontSize: 13, color: C.muted2, whiteSpace: 'nowrap' }}>
            blended rate{' '}
            <span style={{ color: C.text }}>{d.blendedBorrowRatePct.toFixed(2)}%</span>
          </span>
        </div>
        <div style={{ ...colHead, marginTop: 12, display: 'flex', justifyContent: 'space-between' }}>
          <span>Asset</span>
          <span>Balance · rate</span>
        </div>
        {borrowed.map((sym) => (
          <Row
            key={sym}
            symbol={sym}
            amount={amountOf(sym, 'debtUnits')}
            amountUsd={amountOf(sym, 'debtUnits') * priceOf(sym)}
            decimals={pool.assets[sym].decimals === 18 ? 4 : 3}
            right={
              <span style={{ fontSize: 15, color: C.accent, width: 44, textAlign: 'right' }}>
                {pool.assets[sym].borrowRatePct}%
              </span>
            }
          />
        ))}
      </div>

      {/* ── Залог ──────────────────────────────────────────────────── */}
      <div style={PANEL}>
        <div style={TITLE}>Reserved collateral</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap', marginTop: 6 }}>
          <span style={TOTAL}>{usd(d.collateralUsd)}</span>
          <span style={{ fontSize: 13, color: C.muted2, whiteSpace: 'nowrap' }}>
            room to quote <span style={{ color: C.text }}>{usd(d.headroomUsd)}</span>
          </span>
        </div>
        <div style={{ ...colHead, marginTop: 12, display: 'flex', justifyContent: 'space-between' }}>
          <span>Asset</span>
          <span>Balance</span>
        </div>
        {collateral.map((sym) => (
          <Row
            key={sym}
            symbol={sym}
            amount={amountOf(sym, 'lockedCollateralUnits')}
            amountUsd={amountOf(sym, 'lockedCollateralUnits') * priceOf(sym)}
            decimals={pool.assets[sym].decimals === 18 ? 4 : 3}
          />
        ))}
      </div>

      {/* ── Кошелёк ────────────────────────────────────────────────── */}
      <div style={PANEL}>
        <div style={TITLE}>In wallet, to supply</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap', marginTop: 6 }}>
          <span style={TOTAL}>{usd(snap.wallet.reduce((n, w) => n + w.usd, 0))}</span>
          <span style={{ fontSize: 13, color: C.muted2, whiteSpace: 'nowrap' }}>
            {pool.assets.WETH.pauseStatus === 2 ? (
              <span style={{ color: C.warn }}>WETH supply paused</span>
            ) : (
              'supply open'
            )}
          </span>
        </div>
        <div style={{ ...colHead, marginTop: 12, display: 'flex', justifyContent: 'space-between' }}>
          <span>Asset</span>
          <span>Balance · APY</span>
        </div>
        {snap.wallet.map((w) => (
          <Row
            key={w.symbol}
            symbol={w.symbol}
            amount={w.amount}
            amountUsd={w.usd}
            decimals={5}
            right={
              <span style={{ fontSize: 15, color: C.accent, width: 44, textAlign: 'right' }}>
                {w.apyPct}%
              </span>
            }
          />
        ))}
        <div style={{ fontSize: 12, color: C.muted, padding: '10px 0 4px', whiteSpace: 'normal' }}>
          {snap.wallet.some((w) => w.needsWrap)
            ? 'ETH has to be wrapped before it can be supplied — a separate transaction.'
            : ''}
          {pool.assets.WETH.pauseStatus !== 0 &&
            ` Pool: ${PAUSE_LABEL[pool.assets.WETH.pauseStatus]}.`}
        </div>
      </div>
    </div>
  )
}
